import { useLocation } from 'react-router-dom'

/**
 * Represents the current mode based on the URL pathname.
 */
export type Mode = 'add' | 'edit' | 'info' | null

/**
 * Custom hook that determines the current mode based on the URL pathname.
 *
 * This hook analyzes the current location pathname and returns the appropriate mode:
 * - 'add' when the pathname contains '/add'
 * - 'edit' when the pathname contains '/edit'
 * - 'info' when the pathname contains '/info'
 * - null when none of the above patterns match
 *
 * @returns {Mode} The current mode based on the URL pathname
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const mode = useMode()
 *
 *   return (
 *     <div>
 *       {mode === 'add' && <AddForm />}
 *       {mode === 'edit' && <EditForm />}
 *       {mode === 'info' && <InfoView />}
 *     </div>
 *   )
 * }
 * ```
 */
export const useMode = (): Mode => {
  const location = useLocation()

  const getModeFromPathname = (pathname: string): Mode => {
    if (pathname.includes('/add')) return 'add'
    if (pathname.includes('/edit')) return 'edit'
    if (pathname.includes('/info')) return 'info'

    return null
  }

  return getModeFromPathname(location.pathname)
}
